import { useCallback, useState } from 'react';

import type { EmailRequestFormValues } from '@/features/auth/password-reset/components/EmailRequestForm';
import type { VerificationCodeFormValues } from '@/features/auth/password-reset/components/VerificationCodeForm';
import type { AuthError } from '@/features/auth/shared/types/auth';
import { mapClerkError } from '@/features/auth/shared/utils/clerkErrorMapper';
import { createFinalizeNavigate } from '@/features/auth/shared/utils/createFinalizeNavigate';
import { useSignIn } from '@clerk/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

type PasswordResetStep = 'email' | 'code';

export const usePasswordReset = () => {
  const { signIn } = useSignIn();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [step, setStep] = useState<PasswordResetStep>('email');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<AuthError | null>(null);
  const [loading, setLoading] = useState(false);

  const handleReenterEmail = useCallback(() => {
    setStep('email');
    setEmail('');
    setError(null);
  }, []);

  const handleEmailSubmit = useCallback(
    async (data: EmailRequestFormValues) => {
      setLoading(true);
      setError(null);

      try {
        const { error: createError } = await signIn.create({ identifier: data.email });

        if (createError) {
          setError(mapClerkError(createError, t));
          return false;
        }

        const { error: sendCodeError } = await signIn.resetPasswordEmailCode.sendCode();

        if (sendCodeError) {
          setError(mapClerkError(sendCodeError, t));
          return false;
        }

        setEmail(data.email);
        setStep('code');
        return true;
      } catch (err) {
        setError(mapClerkError(err, t));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [signIn, t]
  );

  const handleCodeSubmit = useCallback(
    async (data: VerificationCodeFormValues) => {
      if (!email) return false;

      setLoading(true);
      setError(null);

      try {
        const { error: verifyError } = await signIn.resetPasswordEmailCode.verifyCode({ code: data.code });

        if (verifyError) {
          setError(mapClerkError(verifyError, t));
          return false;
        }

        const { error: passwordError } = await signIn.resetPasswordEmailCode.submitPassword({
          password: data.password
        });

        if (passwordError) {
          setError(mapClerkError(passwordError, t));
          return false;
        }

        if (signIn.status === 'complete') {
          await signIn.finalize({ navigate: createFinalizeNavigate(navigate) });
          toast.success(t('success.passwordResetSent.reset'));
          return true;
        }

        setError({ type: 'general', message: t('error.passwordResetSubmit.unexpectedStatus') });
        return false;
      } catch (err) {
        setError(mapClerkError(err, t));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [signIn, email, navigate, t]
  );

  return {
    step,
    email,
    error,
    loading,
    handleEmailSubmit,
    handleCodeSubmit,
    handleReenterEmail
  };
};
